/**
 * All / Today / Past 7 days / This week filter shown on each kanban column of
 * the Local Moves and Moves in Progress boards. It filters on the day a job
 * landed in its column — `landedAt`, computed by the backend
 * McOpsDashboardService — or on another date the column names (confirmed,
 * intro done, escalation raised).
 */

export const COLUMN_FILTERS = [
  ["all", "All", null],
  ["today", "Today", 1],
  ["past7", "Past 7 days", 7],
  ["week", "This week", "week"],
];

/** The filter's window: a number of days, "week" (since Monday), or null for no limit. */
export const filterWindow = (key) =>
  (COLUMN_FILTERS.find(([k]) => k === key) || COLUMN_FILTERS[0])[2];

const DAY_MS = 86400000;

const startOfDay = (d) => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
};

/** Monday 00:00 of the week `now` is in (weeks run Monday to Sunday). */
export const startOfWeek = (now = new Date()) => {
  const monday = startOfDay(now);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
};

/** The earliest moment a date may have to pass the window; null when unlimited. */
const windowStart = (window, now) => {
  if (window === null || window === undefined) return null;
  if (window === "week") return startOfWeek(now);
  return new Date(now.getTime() - window * DAY_MS);
};

export const isWithin = (date, window, now = new Date(), noFuture = false) => {
  const start = windowStart(window, now);
  if (start === null) return true;
  if (!date) return false;
  const t = new Date(date).getTime();
  // A calendar date ("YYYY-MM-DD") is the whole day: it is still "today" until midnight.
  const endOfDay = (d) => {
    const out = startOfDay(d);
    out.setDate(out.getDate() + 1);
    return out.getTime();
  };
  if (noFuture && t >= endOfDay(now)) return false;
  return t >= start.getTime();
};

export const filterLanded = (
  items,
  filterKey,
  now = new Date(),
  getDate = (j) => j.landedAt,
  noFuture = false,
) => {
  const window = filterWindow(filterKey);
  return (items || []).filter((item) => isWithin(getDate(item), window, now, noFuture));
};

/**
 * Delivery Day in Progress filters by the delivery itself: the local delivery
 * date when one is booked (as a local calendar day), else when the job landed
 * in the column. A delivery booked for a later day is not "today".
 */
export const deliveryDayDate = (job) => {
  const local = job.serviceDates && job.serviceDates.localDeliveryDate;
  return local ? local + "T00:00:00" : job.landedAt;
};

/**
 * A job that arrived on Saturday or Sunday, while the coordinators were off,
 * is flagged until the end of the following Tuesday so it reads as fresh.
 */
export const landedOverWeekend = (landedAt, now = new Date()) => {
  if (!landedAt) return false;
  const landed = new Date(landedAt);
  const day = landed.getDay();
  if (day !== 0 && day !== 6) return false;
  const sunday = startOfDay(landed);
  if (day === 6) sunday.setDate(sunday.getDate() + 1);
  const wednesday = new Date(sunday);
  wednesday.setDate(wednesday.getDate() + 3);
  return now.getTime() < wednesday.getTime();
};
